import AggregateConfig from "./AggregateConfig.json";

const aggregateFunctionHash = AggregateConfig.aggregateFunction;

/**
 * Each function corresponds to transformation function for each stage
 * Input Parameters: stageModel, inputKeywords of the stage, tableHash
 * for the whole query
 * NOTE: Don't use input keywords directly from stageModel. Will break in edit query flow
 * Returns Promise
 */
const keywordTransformation = {
	/**
	 * Find the keyword selected in the `keyword` field from `inputKeywords`
	 * and change the allowMultiple flag to false
	 */
	unwind: (stageModel, inputKeywords, tableHash) => {
		const selectedKeyword = inputKeywords.find(keyword => {
			return stageModel.fields[0].value === keyword.keyword;
		});
		if (selectedKeyword) {
			selectedKeyword.allowMultiple = false;
			return Promise.resolve([...inputKeywords]);
		} else {
			return Promise.reject("Input keyword didn't match with any input keywords");
		}
	},
	/**
	 * Doesn't change the keywords at all
	 */
	sort: (stageModel, inputKeywords, tableHash) => {
		return Promise.resolve([...inputKeywords]);
	},
	/**
	 * Include all the selected values from the `keywords` field and
	 * allow only those values
	 */
	selectKeywords: (stageModel, inputKeywords, tableHash) => {
		return Promise.resolve(
			inputKeywords.filter(keyword => {
				return stageModel.fields[0].value.indexOf(keyword.keyword) !== -1;
			})
		);
	},
	/**
	 * Remove all the selected values from the `keywords` field
	 */
	removeFields: (stageModel, inputKeywords, tableHash) => {
		return Promise.resolve(
			inputKeywords.filter(keyword => {
				return stageModel.fields[0].value.indexOf(keyword.keyword) === -1;
			})
		);
	},
	/**
	 * Doesn't change the keywords at all
	 */
	addFilter: (stageModel, inputKeywords, tableHash) => {
		return Promise.resolve([...inputKeywords]);
	},
	/**
	 * Add all the fields in `selectKeys` to be added in input keywords
	 */
	addFieldLookup: (stageModel, inputKeywords, tableHash) => {
		const foreignTableKeys = stageModel.fields[3].value;
		const filteredForeignTableKeys = foreignTableKeys.filter(key => {
			return inputKeywords.map(keyw => keyw.keyword).indexOf(key) === -1;
		});
		const foreignTableKeywords = tableHash[stageModel.fields[0].value].keywords;
		const toBeAppendedKeys = foreignTableKeywords.filter(
			keyword => filteredForeignTableKeys.indexOf(keyword.keyword) !== -1
		);
		return Promise.resolve([...inputKeywords, ...toBeAppendedKeys]);
	},
	/**
	 * Add the field in `newField` to `inputKeywords`. Type would be `universal`
	 */
	addFieldCalculated: (stageModel, inputKeywords, tableHash) => {
		let filteredInputKeywords = inputKeywords.slice(),
			fieldToBeAppended = {},
			addedFieldsArray = [];
		for (let i = 0; i < stageModel.fields[0].children.length; i++) {
			fieldToBeAppended = {
				allowMultiple: false,
				dataType: "universal",
				foreignField: null,
				foreignTable: null,
				keyType: "attr",
				keyword: stageModel.fields[0].children[i][0].value,
				type: "direct"
			};
			filteredInputKeywords = filteredInputKeywords.filter(key => {
				return key.keyword !== stageModel.fields[0].children[i][0].value;
			});
			addedFieldsArray.push(fieldToBeAppended);
		}
		return Promise.resolve([...filteredInputKeywords, ...addedFieldsArray]);
	},
	/**
	 * Add the field in `newField` to `inputKeywords`. Type would be `universal`
	 */
	currentTime: (stageModel, inputKeywords, tableHash) => {
		let filteredInputKeywords = inputKeywords.slice(),
			fieldToBeAppended = {},
			addedFieldsArray = [];
		for (let i = 0; i < stageModel.fields[0].children.length; i++) {
			fieldToBeAppended = {
				allowMultiple: false,
				dataType: "date",
				foreignField: null,
				foreignTable: null,
				keyType: "attr",
				keyword: stageModel.fields[0].children[i][0].value,
				type: "direct"
			};
			filteredInputKeywords = filteredInputKeywords.filter(key => {
				return key.keyword !== stageModel.fields[0].children[i][0].value;
			});
			addedFieldsArray.push(fieldToBeAppended);
		}
		return Promise.resolve([...filteredInputKeywords, ...addedFieldsArray]);
	},
	/**
	 * Add the new field from the `newField` field. This would be having similar configuration
	 * as `keyword` keyword, just the `keyword` key is updated to value of `newField` field
	 */
	addFieldParent: (stageModel, inputKeywords, tableHash) => {
		const fieldToBeAppended = {
			...inputKeywords.find(key => stageModel.fields[0].value === key.keyword)
		};
		fieldToBeAppended.keyword = stageModel.fields[1].value;
		const filteredInputKeywords = inputKeywords.filter(key => {
			return key.keyword !== stageModel.fields[1].value;
		});
		return Promise.resolve([...filteredInputKeywords, fieldToBeAppended]);
	},
	/**
	 */
	getFieldNames: (stageModel, inputKeywords, tableHash) => {
		const fieldToBeAppended = {
			...inputKeywords.find(key => stageModel.fields[1].value === key.keyword)
		};
		fieldToBeAppended.keyword = stageModel.fields[0].value;
		fieldToBeAppended.dataType = stageModel.fields[2].value.includes("parent")
			? fieldToBeAppended.dataType
			: "universal";
		const filteredInputKeywords = inputKeywords.filter(key => {
			return key.keyword !== stageModel.fields[0].value;
		});
		return Promise.resolve([...filteredInputKeywords, fieldToBeAppended]);
	},
	/**
	 */
	addAttributedFields: (stageModel, inputKeywords, tableHash) => {
		let inputFields = [];
		const fieldsToBeAppended = stageModel.fields[0].children.map(stageFields => {
			inputFields.push(stageFields[0].value);
			let temp = { ...inputKeywords.find(key => stageFields[1].value === key.keyword) };
			return {
				...temp,
				keyword: stageFields[0].value,
				dataType: stageFields[2].value.includes("parent") ? temp.dataType : "universal"
			};
		});
		const filteredInputKeywords = inputKeywords.filter(key => {
			return !inputFields.includes(key.keyword);
		});
		return Promise.resolve([...filteredInputKeywords, ...fieldsToBeAppended]);
	},
	/**
	 */
	pivot: (stageModel, inputKeywords, tableHash) => {
		const fieldsToBeAppended = [
			{
				allowMultiple: false,
				dataType: "universal",
				foreignField: null,
				foreignTable: null,
				keyType: "attr",
				keyword: stageModel.fields[1].value,
				type: "direct"
			},
			{
				allowMultiple: false,
				dataType: "universal",
				foreignField: null,
				foreignTable: null,
				keyType: "attr",
				keyword: stageModel.fields[2].value,
				type: "direct"
			}
		];
		const filteredInputKeywords = inputKeywords.filter(key => {
			return (
				key.keyword !== (stageModel.fields[1].value || stageModel.fields[2].value) &&
				stageModel.fields[0].value.indexOf(key.keyword) === -1
			);
		});
		return Promise.resolve([...filteredInputKeywords, ...fieldsToBeAppended]);
	},
	/**
	 * Take all fields in `keywords`. Add new fields according to `newFields`
	 * in each `aggregateData` array
	 */
	groupBy: (stageModel, inputKeywords, tableHash) => {
		const selectedInputKeywords = stageModel.fields[0].value;
		let filteredInputKeywords = [];
		if (selectedInputKeywords !== undefined) {
			filteredInputKeywords = inputKeywords.filter(key => {
				return selectedInputKeywords.indexOf(key.keyword) !== -1;
			});
		}
		const aggregateData = stageModel.fields[2].children;
		const newAggCols = [];
		aggregateData.forEach(childSet => {
			const aggFunc = childSet[1].value;
			const selectedAggKeyword = inputKeywords.find(key => key.keyword === childSet[2].value);
			const newColConfig = Object.assign(
				{},
				selectedAggKeyword,
				aggregateFunctionHash[aggFunc]["output-datatype"],
				{ keyword: childSet[0].value }
			);
			newAggCols.push(newColConfig);
		});
		return Promise.resolve([...filteredInputKeywords, ...newAggCols]);
	},
	/**
	 * From the `keyword` field, fetch the keyword object and change it's name and display
	 */
	changeField: (stageModel, inputKeywords, tableHash) => {
		let updatedKeywords = [];
		const keywordToBeChanged = stageModel.fields[0].value;
		const updatedKeywordName = stageModel.fields[1].value;
		const updatedKeyAlreadyExists = inputKeywords.find(keyword => {
			return keyword.keyword === updatedKeywordName;
		});
		if (updatedKeyAlreadyExists) {
			updatedKeywords = inputKeywords.filter(keyword => {
				return keyword.keyword !== keywordToBeChanged;
			});
		} else {
			updatedKeywords = inputKeywords.map(keyword => {
				const result = { ...keyword };
				if (result.keyword === keywordToBeChanged) {
					result.keyword = updatedKeywordName;
				}
				return result;
			});
		}
		return Promise.resolve(updatedKeywords);
	},
	/**
	 * From the `keyword` field, fetch multiple keywords and change it's name and display
	 */
	renameMultipleField: (stageModel, inputKeywords, tableHash) => {
		let updatedKeywords = inputKeywords;
		for (let i = 0; i < stageModel.fields[0].children.length; i++) {
			const keywordToBeChanged = stageModel.fields[0].children[i][0].value;
			const updatedKeywordName = stageModel.fields[0].children[i][1].value;
			const updatedKeyAlreadyExists = updatedKeywords.find(keyword => {
				return keyword.keyword === updatedKeywordName;
			});
			if (updatedKeyAlreadyExists) {
				updatedKeywords = updatedKeywords.filter(keyword => {
					return keyword.keyword !== keywordToBeChanged;
				});
			} else {
				updatedKeywords = updatedKeywords.map(keyword => {
					const result = { ...keyword };
					if (result.keyword === keywordToBeChanged) {
						result.keyword = updatedKeywordName;
					}
					return result;
				});
			}
		}
		return Promise.resolve(updatedKeywords);
	},
	/**
	 * Just add the limit number
	 */
	limit: (stageModel, inputKeywords, tableHash) => {
		return Promise.resolve([...inputKeywords]);
	},
	/**
	 * Just add the skip number
	 */
	skip: (stageModel, inputKeywords, tableHash) => {
		return Promise.resolve([...inputKeywords]);
	},
	/*
	 * Fields - fields to be grouped by, Keywords -  keywords to be collated
	 */
	collate: (stageModel, inputKeywords, tableHash) => {
		// inputKeywords = inputKeywords.map(key => key.keyword)
		const selectedInputKeywords = [...stageModel.fields[0].value, ...stageModel.fields[1].value];
		const filteredInputKeywords = inputKeywords.filter(key => {
			return selectedInputKeywords.indexOf(key.keyword) !== -1;
		});
		return Promise.resolve([...filteredInputKeywords]);
	},
	/*
	 * Add new field for organizationId (mandatory for union),
	 * pass tableId for union
	 * selectfields for required fields from new profile
	 */
	union: (stageModel, inputKeywords, tableHash) => {
		let filteredInputKeywords = inputKeywords.slice(),
			stageData = stageModel.fields.slice();
		filteredInputKeywords = filteredInputKeywords.filter(key => {
			return key.keyword !== stageModel.fields[1].value;
		});
		let existingKeywords = inputKeywords.map(key => key.keyword);
		const foreignTableKeywords = tableHash[stageData[0].value].keywords;
		const toBeAppendedKeys = foreignTableKeywords.filter(
			keyword =>
				stageData[1].value.indexOf(keyword.keyword) !== -1 &&
				existingKeywords.indexOf(keyword.keyword) === -1
		);
		return Promise.resolve([...filteredInputKeywords, ...toBeAppendedKeys]);
	},
	/**
	 * Create an extra column of universal type carrying the desired output
	 */
	extractTable: (stageModel, inputKeywords, tableHash) => {
		const fieldsToBeAppended = [
			{
				allowMultiple: false,
				dataType: "universal",
				foreignField: null,
				foreignTable: null,
				keyType: "attr",
				keyword: stageModel.fields[1].value,
				type: "direct"
			}
		];
		const additionalFields = stageModel.fields[2].value.map(elem => {
			return {
				allowMultiple: false,
				dataType: "universal",
				foreignField: null,
				foreignTable: null,
				keyType: "attr",
				keyword: `${stageModel.fields[0].value}_${elem}`,
				type: "direct"
			};
		});
		const filteredInputKeywords = inputKeywords.filter(key => {
			return (
				key.keyword !== (stageModel.fields[1].value && stageModel.fields[0].value) &&
				stageModel.fields[2].value.indexOf(key.keyword) === -1
			);
		});
		return Promise.resolve([...filteredInputKeywords, ...fieldsToBeAppended, ...additionalFields]);
	}
};

export default keywordTransformation;
